import { useMemo } from "react";

import { Badge } from "../../../../components/Badge/Badge";
import type { IAppMetrics } from "../../../../hooks/reactQueryHooks/useBilling";
import {
  formatCurrency,
  formatPercentage,
  shortNumber,
} from "../../../../utils/NumbersFormatting";

interface BadgesProps {
  data?: IAppMetrics;
  cacheItemsCount: number | undefined;
  isLoading: boolean;
}

const Badges: React.FC<BadgesProps> = ({
  data,
  cacheItemsCount,
  isLoading,
}) => {
  const tokensCount = useMemo(() => {
    const paidTokens = data
      ? data.totalActiveInputTokensUsed + data.totalActiveOutputTokensUsed
      : 0;
    const freeTokens = data
      ? data.totalCachedInputTokensUsed + data.totalCachedOutputTokensUsed
      : 0;
    const hitRatio =
      data && data.cachedRequestCount && data.openAiRequestCount
        ? data.cachedRequestCount /
          (data.cachedRequestCount + data.openAiRequestCount)
        : 0;
    return {
      totalTokens: paidTokens + freeTokens,
      paidTokens,
      freeTokens,
      hitRatio,
    };
  }, [data]);

  return (
    <div className="d-grid grid-stats-section">
      <div className="row gy-5 g-xl-5 row-plex widget-container">
        <div className="col-md-4 col-lg-4 col-xl-4 col-xxl-4 mb-xl-0">
          <Badge
            variant="primary"
            content={{
              value: !isLoading
                ? formatCurrency(data?.cogCachePrice || 0)
                : null,
              title: "Total costs",
              tooltipText: "The total costs for all the models.",
            }}
          />
        </div>

        <div className="col-md-4 col-lg-4 col-xl-4 col-xxl-4 mb-xl-0">
          <Badge
            variant="success"
            content={{
              value: !isLoading ? formatCurrency(data?.costSavings || 0) : null,
              title: "Cost savings",
              tooltipText:
                "Amount of cost savings based on token discounts and caching.",
            }}
          />
        </div>

        <div className="col-md-4 col-lg-4 col-xl-4 col-xxl-4 mb-xl-0">
          <Badge
            variant="info"
            content={{
              value: !isLoading ? shortNumber(tokensCount.totalTokens) : null,
              title: "Total tokens",
              tooltipText:
                "Total amount of tokens. The tokens that are generated from cache are free of charge.",
              rightItems: [
                {
                  text: !isLoading ? shortNumber(tokensCount.paidTokens) : null,
                  subtitle: "paid tokens",
                },
                {
                  text: !isLoading ? shortNumber(tokensCount.freeTokens) : null,
                  subtitle: "free tokens",
                },
              ],
            }}
          />
        </div>
      </div>
      <div className="row gy-5 g-xl-5 row-plex widget-container">
        <div className="col-md-6 col-lg-6 col-xl-6 col-xxl-6 mb-xl-0">
          <Badge
            variant="primary"
            content={{
              value: !isLoading ? formatPercentage(tokensCount.hitRatio) : null,
              title: "Cache hit ratio",
              tooltipText:
                "The percentage of answers coming from the cache. The higher the number, the better.",
            }}
          />
        </div>

        <div className="col-md-6 col-lg-6 col-xl-6 col-xxl-6 mb-xl-0">
          <Badge
            variant="info"
            content={{
              value: !isLoading ? shortNumber(cacheItemsCount || 0) : null,
              title: "Total cache items",
              tooltipText: "The total amount of cache items for this project.",
            }}
          />
        </div>
      </div>
    </div>
  );
};

export { Badges };
